import { RECORDING_BITS_PER_SECOND } from "../utils/constants";

export class VideoRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt: number = 0;
  private onRecordingError: (() => void) | null = null;

  /** Fires immediately on a mid-recording error, unlike stop()'s onerror which only fires when stop() is later called. */
  onError(callback: () => void): void {
    this.onRecordingError = callback;
  }

  start(stream: MediaStream): void {
    this.chunks = [];

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm";

    this.recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: RECORDING_BITS_PER_SECOND,
    });

    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.chunks.push(e.data);
      }
    };

    this.recorder.addEventListener("error", () => {
      this.onRecordingError?.();
    });

    this.startedAt = Date.now();
    this.recorder.start();
  }

  stop(): Promise<{ blob: Blob; durationSeconds: number }> {
    return new Promise((resolve, reject) => {
      if (!this.recorder || this.recorder.state === "inactive") {
        reject(new Error("Recorder is not active"));
        return;
      }

      const stoppedAt = Date.now();

      this.recorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: "video/webm" });
        const durationSeconds = parseFloat(
          ((stoppedAt - this.startedAt) / 1000).toFixed(2)
        );
        this.chunks = [];
        resolve({ blob, durationSeconds });
      };

      this.recorder.onerror = () => {
        reject(new Error("Recording failed"));
      };

      this.recorder.stop();
    });
  }

  isRecording(): boolean {
    return this.recorder?.state === "recording";
  }
}
